import { intersection } from 'lodash-es';
import i18n from '../../../../i18n';
import utility from '../../../../services/utility-service';

export default {
  /**
   * @returns {*[]}
   * @param state
   */
  getSupplierPaymentsAccumulationTableHeaders(state) {
    const headers = [
      {
        value: 'supplier.name',
        text: i18n.t('supplierPayments.lists.types.accumulation.supplier'),
        align: 'center',
        sortable: true,
        class: 'px-2',
      },
      {
        value: 'product.name',
        text: i18n.t('supplierPayments.lists.types.accumulation.product'),
        align: 'center',
        sortable: true,
        class: 'px-2',
      },
      {
        value: 'payments.total.live',
        text: i18n.t('supplierPayments.lists.types.accumulation.live'),
        align: 'center',
        sortable: true,
        class: 'px-2',
      },
      {
        value: 'payments.total.cancelled',
        text: i18n.t('supplierPayments.lists.types.accumulation.cancelled'),
        align: 'center',
        sortable: true,
        class: 'px-2',
      },
    ];
    state.filter.data.brands.forEach((brand) => {
      headers.push({
        value: `payments.brands.${brand.id}.total`,
        text: brand.name,
        align: 'center',
        sortable: true,
        class: 'px-2',
      });
    });
    return headers;
  },

  /**
   * @returns {*[]}
   */
  getSupplierPaymentsPaymentTableHeaders() {
    return [
      {
        value: 'createdAt',
        text: i18n.t('supplierPayments.lists.types.payments.date'),
        align: 'center',
        sortable: true,
      },
      {
        value: 'id',
        text: i18n.t('supplierPayments.lists.types.payments.payment'),
        align: 'center',
        sortable: true,
      },
      {
        value: 'product.name',
        text: i18n.t('supplierPayments.lists.types.payments.product'),
        align: 'center',
        sortable: true,
      },
      {
        value: 'supplier.name',
        text: i18n.t('supplierPayments.lists.types.payments.supplier'),
        align: 'center',
        sortable: true,
      },
      {
        value: 'paid',
        text: i18n.t('supplierPayments.lists.types.payments.paid'),
        align: 'center',
        sortable: true,
      },
      {
        value: 'currency',
        text: i18n.t('supplierPayments.lists.types.payments.currency'),
        align: 'center',
        sortable: true,
      },
      {
        value: 'paidInEuro',
        text: i18n.t('supplierPayments.lists.types.payments.euro'),
        align: 'center',
        sortable: true,
      },
    ];
  },

  /**
   * @param state
   * @returns {*}
   */
  getSupplierPaymentsAccumulationTableRows(state) {
    if (state.tableData.rows.length === 0) {
      return [];
    }
    // A supplier whose every amount shows as 0,00 - live, cancelled and each brand's - can be
    // left out.
    const isZero = amount => Math.abs(amount || 0) < 0.005;
    const hasAmounts = row => !isZero(row.payments.total.live)
      || !isZero(row.payments.total.cancelled)
      || Object.values(row.payments.brands || {}).some(brand => !isZero(brand.total)
        || !isZero(brand.live)
        || !isZero(brand.cancelled));
    const rows = state.filter.data.hideZeroSuppliers
      ? state.tableData.rows.filter(hasAmounts)
      : state.tableData.rows;
    return [
      { type: 1 },
      { type: 2 },
    ].concat(rows);
  },

  /**
   * @param state
   * @returns {T[]}
   */
  getSuppliersForFilter(state) {
    let selectedDomains = [...state.filter.data.selectedDomains];
    if (!state.filter.data.selectedDomains.length &&
      state.filter.data.selectedBrands.length) {
      state.filter.data.selectedBrands.forEach((brand) => {
        const domains = state.filter.data.domains.filter(
          domain => domain.brand.id === brand,
        ).map(domain => domain.id);
        selectedDomains = selectedDomains.concat(domains);
      });
    }
    return state.filter.data.suppliers.filter((supplier) => {
      if (state.filter.data.selectedSuppliers.includes(supplier.id)) {
        return true;
      }
      if (selectedDomains.length &&
        intersection(selectedDomains, supplier.domains).length === 0) {
        return false;
      }
      if (state.filter.data.selectedProductTypes.length &&
        !state.filter.data.selectedProductTypes.includes(supplier.productTypeId)) {
        return false;
      }
      return !state.filter.data.supplierSearch ||
        supplier.name.toLowerCase()
          .indexOf(state.filter.data.supplierSearch.toLowerCase()) !== -1;
    });
  },

  /**
   * @param state
   * @returns {*}
   */
  getDomainsForFilter(state) {
    if (state.filter.data.selectedBrands.length === 0) {
      return state.filter.data.domains;
    }
    return state.filter.data.domains.filter((domain) => {
      if (state.filter.data.selectedDomains.includes(domain.id)) {
        return true;
      }
      return state.filter.data.selectedBrands.includes(domain.brand.id);
    });
  },

  /**
   * @param state
   * @returns {*[]}
   */
  getDownloadSearchInfo(state) {
    const lastSearch = state.filter.lastSearch;
    const filterData = state.filter.data;
    if (!lastSearch.performed) {
      return [];
    }

    return [
      {
        label: i18n.t('payments.filters.arrival'),
        value: `${lastSearch.date.arrivalFromDate} - ${lastSearch.date.arrivalToDate}`,
      },
      {
        label: i18n.t('payments.filters.paidDate'),
        value: `${lastSearch.date.paidDateFromDate} - ${lastSearch.date.paidDateToDate}`,
      },
      {
        label: i18n.t('payments.filters.brands'),
        value: `${utility.getNameFromFirstSelectedItem(
          lastSearch.data.selectedBrands,
          filterData.brands,
        )}`,
      },
      {
        label: i18n.t('payments.filters.domains'),
        value: `${utility.getNameFromFirstSelectedItem(
          lastSearch.data.selectedDomains,
          filterData.domains,
        )}`,
      },
      {
        label: i18n.t('payments.filters.productTypes'),
        value: `${utility.getNameFromFirstSelectedItem(
          lastSearch.data.selectedProductTypes,
          filterData.productTypes,
        )}`,
      },
      {
        label: i18n.t('payments.filters.suppliers'),
        value: `${utility.getNameFromFirstSelectedItem(
          lastSearch.data.selectedSuppliers,
          filterData.suppliers,
        )}`,
      },
    ];
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/payments/supplierPayments/getters.js